import React, { useCallback, useRef, useEffect, useState } from 'react';
import { VideoPlayer } from '../../VideoPlayer/VideoPlayer';

export interface VideosSwitchProps {
  videoId: string | null;
  videoUrl: string | null;
  videoPlayEnded: () => void;
  setShowAnswerVideo: React.Dispatch<boolean>;
  waitingVideoId: string | null;
  waitingVideoUrl: string | null;
  showAnswerVideo: boolean;
  isWaiting: boolean;
}

export const VideosSwitch: React.FunctionComponent<VideosSwitchProps> = React.memo(
  ({
    videoId,
    videoUrl,
    videoPlayEnded,
    setShowAnswerVideo,
    waitingVideoId,
    waitingVideoUrl,
    showAnswerVideo,
    isWaiting,
  }) => {
    const waitingVideoDiv = useRef<HTMLDivElement | null>(null);
    const waitingPlayer = useRef<any | null>(null);
    const answerPlayer = useRef<any | null>(null);
    const fadeIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isSkipping = useRef(false);
    const lastShowAnswerVideo = useRef(showAnswerVideo);
    const isWaitingRef = useRef(isWaiting);
    const [waitingVideoReady, setWaitingVideoReady] = useState(false);
    const [answerVideoReady, setAnswerVideoReady] = useState(false);

    useEffect(() => {
      isWaitingRef.current = isWaiting;
    }, [isWaiting]);

    useEffect(() => {
      setWaitingVideoReady(false);
    }, [waitingVideoId]);

    useEffect(() => {
      setAnswerVideoReady(false);
    }, [videoId]);

    useEffect(() => {
      if (lastShowAnswerVideo.current === true && showAnswerVideo === false && !isWaiting) {
        isSkipping.current = true;

        if (fadeIntervalRef.current) {
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
        }

        // Fade out the answer video audio
        if (answerPlayer.current && !answerPlayer.current.isDisposed()) {
          const player = answerPlayer.current;
          const startVolume = player.volume();
          const fadeSteps = 30; // Number of steps for smooth fade
          const stepDuration = 1200 / fadeSteps; // ~10ms per step
          let currentStep = 0;

          fadeIntervalRef.current = setInterval(() => {
            currentStep++;
            const newVolume = startVolume * (1 - currentStep / fadeSteps);

            if (currentStep >= fadeSteps || newVolume <= 0) {
              // Fade complete - pause and reset
              if (player && !player.isDisposed()) {
                player.volume(0);
                player.pause();
                player.currentTime(0);
              }
              if (fadeIntervalRef.current) {
                clearInterval(fadeIntervalRef.current);
                fadeIntervalRef.current = null;
              }
            } else {
              if (player && !player.isDisposed()) {
                player.volume(newVolume);
              }
            }
          }, stepDuration);
        }

        setTimeout(() => {
          if (waitingPlayer.current && !waitingPlayer.current.isDisposed()) {
            waitingPlayer.current.currentTime(0);
            waitingPlayer.current.play();
          }

          videoPlayEnded();
        }, 1300);
      }

      lastShowAnswerVideo.current = showAnswerVideo;
    }, [showAnswerVideo, isWaiting, videoPlayEnded]);

    useEffect(() => {
      if (isWaiting && isSkipping.current) {
        isSkipping.current = false;
      }
    }, [isWaiting]);

    useEffect(() => {
      if (isWaiting && showAnswerVideo) {
        setShowAnswerVideo(false);
      }
    }, [isWaiting, showAnswerVideo, setShowAnswerVideo]);

    const onWaitingVideoCanPlay = useCallback(() => {
      if (waitingPlayer.current && !waitingPlayer.current.isDisposed()) {
        waitingPlayer.current.play();
      }

      setTimeout(() => {
        setWaitingVideoReady(true);
      }, 10);
    }, []);

    const onCanPlay = useCallback(() => {
      setTimeout(() => {
        setAnswerVideoReady(true);
      }, 10);

      if (!isWaiting) {
        setTimeout(() => {
          setShowAnswerVideo(true);
        }, 50);
      }

      if (waitingPlayer.current && !waitingPlayer.current.isDisposed()) {
        waitingPlayer.current.pause();
      }

      if (answerPlayer.current && !answerPlayer.current.isDisposed()) {
        answerPlayer.current.volume(1);
      }
    }, [setShowAnswerVideo, isWaiting]);

    const onFinished = useCallback(() => {
      const currentIsWaiting = isWaitingRef.current;

      if (isSkipping.current) {
        return;
      }

      if (currentIsWaiting) {
        return;
      }

      if (waitingPlayer.current && !waitingPlayer.current.isDisposed()) {
        waitingPlayer.current.currentTime(0);
        waitingPlayer.current.play();
      }

      setTimeout(() => {
        videoPlayEnded();
      }, 50);
    }, [videoPlayEnded]);

    useEffect(() => {
      if (isWaiting && waitingVideoReady && waitingPlayer.current && !waitingPlayer.current.isDisposed()) {
        if (waitingPlayer.current.paused()) {
          waitingPlayer.current.play();
        }
      }
    }, [isWaiting, waitingVideoReady]);

    useEffect(() => {
      return () => {
        if (fadeIntervalRef.current) {
          clearInterval(fadeIntervalRef.current);
          fadeIntervalRef.current = null;
        }
      };
    }, []);

    return (
      <div className="roleplay__person default-video default-video--visible">
        <div
          className={`waiting-video ${waitingVideoReady && (isWaiting || !showAnswerVideo) ? 'waiting-video--ready' : ''}`}
          ref={waitingVideoDiv}
        >
          {waitingVideoId && waitingVideoUrl && (
            <VideoPlayer
              key={`waiting-${waitingVideoId}`}
              accountId={'3845398857001'}
              videoId={waitingVideoId}
              videoUrl={waitingVideoUrl}
              loop={true}
              onCanPlay={onWaitingVideoCanPlay}
              onPlayerReady={(player) => {
                waitingPlayer.current = player;
              }}
            />
          )}
        </div>
        <div
          className={`answer-video ${showAnswerVideo ? 'answer-video--visible' : ''} ${answerVideoReady ? 'answer-video--ready' : ''}`}
          id="answer-video-div"
        >
          {videoId && videoUrl && (
            <VideoPlayer
              key={`answer-${videoId}`}
              accountId={'3845398857001'}
              videoId={videoId}
              videoUrl={videoUrl}
              onEnded={onFinished}
              onCanPlay={onCanPlay}
              unmuted={true}
              onPlayerReady={(player) => {
                answerPlayer.current = player;
              }}
            />
          )}
        </div>
      </div>
    );
  },
);
